// A escada: cores e rotulos dos niveis, espelhando o backoffice (CourseCard).
// Fonte unica para os componentes do connect (ModuleCard, ModuleHero, listas).
import i18n from "@/plugins/i18n";

export const TIER_COLORS = {
  0: "#771BA0", // Beginner
  1: "#771BA0",
  2: "#9923CD", // Essential
  3: "#9923CD",
  4: "#E8276F", // Pre-Intermediate (rosa da marca)
  5: "#E8276F",
  6: "#FF2B7A", // Intermediate
  7: "#FF2B7A",
  8: "#FF5D2A", // Upper-Intermediate
  9: "#FF5D2A",
  10: "#E85526", // Advanced I
  11: "#E85526", // Advanced II (o cume) - mesmo tier de Advanced
  99: "#00A379", // Conversation - trilha a parte (teal)
  100: "#00A379",
  101: "#00A379",
  102: "#B7791F", // Instrumental - trilha a parte (ocre), card 17trt6je3uq
  103: "#6E6A79", // Outros - trilha a parte (cinza)
};

export const TIER_LABELS = {
  0: "Beginner",
  1: "Beginner",
  2: "Essential I",
  3: "Essential II",
  4: "Pre-Intermediate",
  5: "Pre-Intermediate",
  6: "Intermediate",
  7: "Intermediate",
  8: "Upper-Intermediate",
  9: "Upper-Intermediate",
  10: "Advanced I",
  11: "Advanced II",
};

export function tierColor(level) {
  return TIER_COLORS[Number(level)] || "#9923CD";
}

export function tierLabel(level) {
  const n = Number(level);
  if ([99, 100, 101].includes(n)) return i18n.t(`enums.tierConversation.${n}`);
  if ([102, 103].includes(n)) return i18n.t(`enums.trackLevel.${n}`);
  return TIER_LABELS[n] || `Level ${n}`;
}

// Categoria da aula (CourseClasses.category): rotulo traduzido + cor.
// A cor da categoria conduz o accent da tela de conteudo da aula.
const CATEGORY_COLORS = {
  CONVERSATION: "#00A379",
  GRAMMAR: "#9923CD",
  VOCABULARY: "#E8276F",
  REVISION: "#FF5D2A",
  WRITING: "#771BA0",
  SITUATIONAL: "#FF2B7A",
  SUPPORT: "#6E6A79",
  REVISION_EXERCISE: "#E85526",
  GUIDE: "#5F5F61",
  READING: "#00A379",
  INSTRUMENTAL: "#771BA0",
  OTHER: "#6E6A79",
};

export const LESSON_CATEGORIES = Object.fromEntries(
  Object.entries(CATEGORY_COLORS).map(([key, color]) => [
    key,
    {
      get label() {
        return i18n.t(`enums.category.${key}`);
      },
      color,
    },
  ])
);

export function categoryColor(category) {
  return LESSON_CATEGORIES[category]?.color || "#9923CD";
}

export function categoryLabel(category) {
  return LESSON_CATEGORIES[category]?.label || i18n.t("enums.category.default");
}

// Rampa exibida na legenda do cabecalho de modulos.
export const RAMP_TIERS = [
  { key: "Beginner", color: "#771BA0" },
  { key: "Essential", color: "#9923CD" },
  { key: "Pre-Intermediate", color: "#E8276F" },
  { key: "Intermediate", color: "#FF2B7A" },
  { key: "Upper-Interm.", color: "#FF5D2A" },
  { key: "Advanced", color: "#E85526" },
];

// Posição da aula na sequência do módulo, a partir do índice 0 da lista que a
// API já devolve ordenada ("01", "02"...). Card 86ajw35m1.
export function lessonPosition(index) {
  return String(Number(index) + 1).padStart(2, "0");
}

// Card 86akptec0: modulo exclusivo de professores (treinamento). Nao e degrau
// da escada: sai sem numeral, sem medidor e com cor propria.
export const TRAINING_COLOR = "#3D5A80";

export function isTrainingModule(course) {
  if (!course) return false;
  if (typeof course.isTraining === "boolean") return course.isTraining;
  return (
    course.sharedWithCustomers === false && course.sharedWithPartners !== false
  );
}

// Card 86akqwgcv: filtro por nível da aba Materiais do professor, espelhando a
// rampa clicável do backoffice. Cada degrau cobre dois níveis do enum
// EnglishLevels; Conversation (99-101), Instrumental (102) e Outros (103) são
// trilhas à parte (`track`), que só aparecem quando há módulo delas.
export const LEVEL_FILTERS = [
  ...RAMP_TIERS.map((tier, index) => ({
    key: tier.key,
    color: tier.color,
    levels: [index * 2, index * 2 + 1],
  })),
  {
    key: "Conversation",
    color: "#00A379",
    levels: [99, 100, 101],
    track: true,
  },
  ...[102, 103].map((level) => ({
    key: `track-${level}`,
    get label() {
      return i18n.t(`enums.trackLevel.${level}`);
    },
    color: TIER_COLORS[level],
    levels: [level],
    track: true,
  })),
];

export function matchesLevelFilter(course, filter) {
  if (!filter) return true;
  if (!course || course.level == null || course.level === "") return false;
  // `level` chega como string ("4") -- compara como número, nunca com ===.
  return filter.levels.includes(Number(course.level));
}

// Card 86akqwgkw: busca de aula pelo nome, sem diferenciar acento e caixa.
export function normalizeSearch(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

// Aulas dos módulos cujo título contém o termo, com o módulo de origem.
// Usa o índice leve `lessons` que a API devolve na listagem de módulos (ou
// `classes`, quando a tela já tem as aulas completas).
export function findLessons(modules, query) {
  const term = normalizeSearch(query);
  if (!term) return [];

  const results = [];
  (modules || []).forEach((module) => {
    (module.lessons || module.classes || []).forEach((lesson, index) => {
      if (normalizeSearch(lesson.title).includes(term)) {
        results.push({ module, lesson, position: index });
      }
    });
  });
  return results;
}
